import { action, computed, makeObservable, observable } from 'mobx';
import {
  ActionObject,
  ActionUiPresenter,
  Document,
  DocumentAction,
  DocumentDomainProperties,
  DocumentRestriction,
  DocumentStatus,
  FileSource,
  UploadStep,
} from 'types/documentsService';
import { RequestSignatureModalStore } from 'store/modules/upload-documents/ui-stores/requestSignatureModalStore';
import { EditDocumentModalStore } from 'store/modules/upload-documents/ui-stores/editDocumentModalStore';
import { SupportedUserRoles, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import ModalDelegate from 'store/general/modalDelegate';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { actionsFiltersPipeline } from './actionRules';
import { EditTemplateModalStore } from './ui-stores/editTemplateModalStore';

interface ActionsUiStores {
  requestSignatureModalStore: RequestSignatureModalStore | null;
  editDocumentModalStore: EditDocumentModalStore | null;
  editTemplateModalStore: EditTemplateModalStore | null;
  warningModalDelegates: Record<MissclikableAction, ModalDelegate>;
}

export interface ConstructorParams {
  document: Document;
  userRoles: SupportedUserRoles[];
  pageType: UploadStep;
  periodId: string;
  refreshData: () => Promise<void>;
  setPageLoading: (loading: boolean) => void;
  getExistingDocumentTypes: () => string[];
}

export enum MissclikableAction {
  BYPASS = DocumentAction.BYPASS,
  DISCARD = DocumentAction.DISCARD,
  REMOVE_UPLOAD = DocumentAction.REMOVE_UPLOAD,
  APPROVE = DocumentAction.APPROVE,
  REJECT = DocumentAction.REJECT,
  SEND_SIGN_REMINDER = DocumentAction.SEND_SIGN_REMINDER,
}

export type WarningModals = Record<MissclikableAction, ModalDelegate>;

export default class DocumentDelegate {
  private static handleDocumentDomainProps(document: Document, pageType: UploadStep): DocumentDomainProperties {
    const excludeActions: DocumentAction[] = [];

    if (!document.eligibleForSignature) {
      excludeActions.push(DocumentAction.REQUEST_FOR_SIGNATURE);
    }

    if (document.fileSource !== FileSource.CUSTOM) {
      excludeActions.push(DocumentAction.DISCARD);
      excludeActions.push(DocumentAction.EDIT);
    }

    if (document.restrictions && document.restrictions[pageType] !== DocumentRestriction.BYPASS_ALLOWED) {
      excludeActions.push(DocumentAction.BYPASS);
    }

    return { excludeActions };
  }

  public get passedRestriction(): boolean {
    const docNotUploaded = this.documentInfo.status === DocumentStatus.NOT_UPLOADED;
    const docBypassed = this.documentInfo.status === DocumentStatus.BYPASSED;
    const docApproved = this.documentInfo.status === DocumentStatus.APPROVED;
    const docRejected = this.documentInfo.status === DocumentStatus.REJECTED;

    switch (this._activeRestriction) {
      case DocumentRestriction.UPLOAD_REQUIRED:
        return !docNotUploaded && !docBypassed && !docRejected;
      case DocumentRestriction.APPROVE_REQUIRED:
        return docApproved;
      default:
        return true;
    }
  }

  private _documentInfo: Document;
  private _isLoading: boolean = false;
  private _actionList: ActionObject[];
  private _refreshData: () => Promise<void>;
  private _setPageLoading: (loading: boolean) => void;
  private _requestSignatureModalStore: RequestSignatureModalStore | null = null;
  private _editDocumentModalStore: EditDocumentModalStore | null = null;
  private _editTemplateModalStore: EditTemplateModalStore | null = null;
  private _warningModalDelegates = {
    [MissclikableAction.BYPASS]: new ModalDelegate(),
    [MissclikableAction.DISCARD]: new ModalDelegate(),
    [MissclikableAction.REMOVE_UPLOAD]: new ModalDelegate(),
    [MissclikableAction.APPROVE]: new ModalDelegate(),
    [MissclikableAction.REJECT]: new ModalDelegate(),
    [MissclikableAction.SEND_SIGN_REMINDER]: new ModalDelegate(),
  };

  private _activeRestriction: DocumentRestriction;

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get documentInfo(): Document {
    return this._documentInfo;
  }

  public get actionsUiStores(): ActionsUiStores {
    return {
      requestSignatureModalStore: this._requestSignatureModalStore,
      editDocumentModalStore: this._editDocumentModalStore,
      editTemplateModalStore: this._editTemplateModalStore,
      warningModalDelegates: this._warningModalDelegates,
    };
  }

  public get activeRestriction(): DocumentRestriction {
    return this._activeRestriction;
  }

  private _openSignatureModal(): void {
    this._requestSignatureModalStore?.modalDelegate.open();
  }

  private _openEditModal(): void {
    this._editDocumentModalStore?.openEditModal();
  }

  private _openEditTemplateModal(): void {
    this._editTemplateModalStore?.openEditModal();
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  /** A set of actions available for the current status. */
  public get availableActions(): ActionUiPresenter[] {
    const documentId = this._documentInfo.id;
    return this._actionList.map((actionObj) => {
      const handler = async (): Promise<void> => {
        await actionObj.handler({
          interactions: {
            setCardIsLoading: this.setIsLoading,
            setPageIsLoading: this._setPageLoading,
            refreshData: this._refreshData,
          },
          ui: {
            openRequestSignModal: this._openSignatureModal.bind(this),
            openEditModal: this._openEditModal.bind(this),
            openEditTemplateModal: this._openEditTemplateModal.bind(this),
            showSentNotification: () => toastManager.addToast('Reminder has been sent!', ToastColorTypes.SUCCESS),
            showBeError: (error: string) => toastManager.addToast(error, ToastColorTypes.WARNING),
          },
          static: {
            documentId,
          },
        });
      };
      return {
        type: actionObj.type,
        name: actionObj.name,
        isDisabled: actionObj.isDisabled,
        handler,
      };
    });
  }

  constructor({
    document,
    userRoles,
    pageType,
    periodId,
    refreshData,
    setPageLoading,
    getExistingDocumentTypes,
  }: ConstructorParams) {
    this._documentInfo = document;
    this._activeRestriction =
      document.restrictions && document.restrictions[pageType]
        ? document.restrictions[pageType]
        : DocumentRestriction.UPLOAD_REQUIRED;
    this._actionList = actionsFiltersPipeline({
      userRoles,
      documentStatus: document.status,
      pageType,
      domainProperties: DocumentDelegate.handleDocumentDomainProps(document, pageType),
    });
    this._setPageLoading = setPageLoading;
    this._refreshData = refreshData;
    this._requestSignatureModalStore = new RequestSignatureModalStore({
      documentId: document.id,
      periodId,
      refreshData,
    });
    this._editDocumentModalStore = new EditDocumentModalStore({
      document,
      onSubmit: refreshData,
      getExistingDocumentTypes,
    });
    this._editTemplateModalStore = new EditTemplateModalStore({
      document,
      onSubmit: refreshData,
    });

    makeObservable<
      DocumentDelegate,
      | '_documentInfo'
      | '_openSignatureModal'
      | '_editDocumentModalStore'
      | '_isLoading'
      | '_openEditModal'
      | '_openEditTemplateModal'
      | '_requestSignatureModalStore'
      | '_activeRestriction'
    >(this, {
      _isLoading: observable,
      _documentInfo: observable,
      _requestSignatureModalStore: observable,
      _editDocumentModalStore: observable,
      _activeRestriction: observable,

      isLoading: computed,
      documentInfo: computed,
      availableActions: computed,
      actionsUiStores: computed,
      activeRestriction: computed,
      passedRestriction: computed,

      _openEditModal: action.bound,
      _openEditTemplateModal: action.bound,
      _openSignatureModal: action.bound,
      setIsLoading: action.bound,
    });
  }
}
